import React from "react";

import Row from "antd/es/row";
import Tag from "antd/es/tag";
import filter from "lodash-es/filter";
import sumBy from "lodash-es/sumBy";
import { FormattedMessage, useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import TextWithLine from "@app/component/typography/TextWithLine";
import localSettings from "@app/config/LocalSettings";
import FunnelStatsData from "@app/scene/reports/store/FunnelStatsData";
import StageStatsData from "@app/scene/reports/store/StageStatsData";

import styles from "../Header/Header.module.scss";
import MetricCard from "../MetricCard";

import Charts from "./components/Charts";
import DealListModal from "./components/Charts/ProgressionChart/DealListModal";
import dealStyles from "./DealsCard.module.scss";

interface Props {
  funnelsStatsData: FunnelStatsData;
  selectedFunnel?: Funnel;
  selectedStage?: Stage;
  stageStatsData?: StageStatsData;
  userIds: User["id"][];
}

const StatsForStage: React.FC<Props> = ({
  funnelsStatsData,
  selectedFunnel,
  selectedStage,
  stageStatsData,
  userIds,
}) => {
  const intl = useIntl();

  const [isStageReportShown, showStageReport, hideStageReport] = useBoolean();

  const dealsWon = funnelsStatsData.orgStats.dealsClosedWon || 0;
  const dealsLost = funnelsStatsData.orgStats.dealsClosedLost || 0;
  const grossWonRevenue = funnelsStatsData.orgStats.revenueClosedWon || 0;
  const grossLostRevenue = funnelsStatsData.orgStats.revenueClosedLost || 0;

  const stageSummary = selectedStage
    ? filter(funnelsStatsData.stageSummary, { stage: { id: selectedStage.id } })
    : funnelsStatsData.stageSummary;
  const totalDealsCount = sumBy(stageSummary, "totalDeals");
  const grossAmountOpen = sumBy(stageSummary, "totalValue");
  const avgDealSize = totalDealsCount > 0 ? grossAmountOpen / totalDealsCount : 0;
  const adjustedAmountOpen = selectedStage
    ? grossAmountOpen * selectedStage.weight
    : funnelsStatsData.stageSummary.reduce(
        (memo, curr) => memo + curr.totalValue * curr.stage.weight,
        0
      );

  return (
    <>
      <br />
      <TextWithLine>
        <FormattedMessage
          id="reports.deals.quickMetrics"
          defaultMessage="Quick Metrics"
          description="Title for Quick Metrics section on Deals screen."
        />
      </TextWithLine>

      {/* number of deals */}
      <div className={styles.subSection}>
        <div className={styles.subTitle}>
          <FormattedMessage
            id="reports.deals.pipelineMetrics"
            defaultMessage="Pipeline metrics for {hasStage, select, true {{stage}} other {<tag>All stages</tag>}}"
            description="Title for Quick Metrics section on Deals screen."
            values={{
              hasStage: !!selectedStage,
              stage: <strong>&quot;{selectedStage?.name}&quot;</strong>,
              tag: (text) => <strong>&quot;{text}&quot;</strong>,
            }}
          />
          <Tag className={styles.roleTag} color="blue">
            <FormattedMessage
              id="reports.deals.quickMetrics.dealCount"
              defaultMessage="{count} {count, plural, one {Deal} other {Deals}}"
              description="Data for Quick Metrics Deals tag on Deals screen."
              values={{
                count: totalDealsCount,
              }}
            />
          </Tag>
        </div>
        <Button onClick={showStageReport} type="link">
          <FormattedMessage
            id="reports.deals.closingMetrics.detailedReport"
            defaultMessage="Detailed report"
            description="Report link for Closing Metrics section on Deals screen."
          />
        </Button>
      </div>

      <div className={dealStyles.metricSection}>
        {/* gross amount open */}
        <MetricCard
          title={intl.formatMessage({
            id: "reports.deals.pipelineMetrics.grossAmount",
            defaultMessage: "Gross Amount",
            description: "Title for Gross Amount on Deals screen.",
          })}
          value={intl.formatNumber(grossAmountOpen, {
            currency: localSettings.getCurrencyCode(),
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
            style: "currency",
          })}
        />

        {/* adjusted amount open */}
        <MetricCard
          title={
            <TextWithInfo
              info={intl.formatMessage({
                id: "reports.deals.pipelineMetrics.adjustedAmount.explanation",
                defaultMessage:
                  "Adjusted amount is calculated by multiplying the total amount of revenue in each deal stage by the probability of that stage",
                description:
                  "Adjusted amount explanation. It'll show up as a tooltip over `Adjusted Amount` card at Deals report",
              })}
              inline
            >
              <FormattedMessage
                id="reports.deals.pipelineMetrics.adjustedAmount"
                defaultMessage="Adjusted Amount"
                description="Title for Adjusted Amount on Deals screen."
              />
            </TextWithInfo>
          }
          value={intl.formatNumber(adjustedAmountOpen, {
            currency: localSettings.getCurrencyCode(),
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
            style: "currency",
          })}
        />

        {/* average deal size */}
        <MetricCard
          title={intl.formatMessage({
            id: "reports.deals.pipelineMetrics.dealSize",
            defaultMessage: "Avg. Deal Size",
            description: "Title for Avg. Deal Size on Deals screen.",
          })}
          value={intl.formatNumber(avgDealSize, {
            currency: localSettings.getCurrencyCode(),
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
            style: "currency",
          })}
        />

        {!selectedStage || selectedStage?.type === DealStageType.DEFAULT ? (
          <>
            <MetricCard
              title={intl.formatMessage({
                id: "reports.deals.pipelineMetrics.dealLength",
                defaultMessage: "Avg. Days in Stage",
                description: "Title for Days in Stage on Deals screen.",
              })}
              value={intl.formatMessage(
                {
                  id: "reports.deals.pipelineMetrics.dealLength.content",
                  defaultMessage: "{length} {length, plural, one {Day} other {Days}}",
                  description: "Data for Days in Stage on Deals screen.",
                },
                {
                  length: intl.formatNumber(stageStatsData?.avgDaysInStage ?? 0, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }),
                }
              )}
            />
            <MetricCard
              title={intl.formatMessage({
                id: "reports.deals.pipelineMetrics.dealsRotting",
                defaultMessage: "Deals rotting",
                description: "Title for Deals rotting Length on Deals screen.",
              })}
              value={stageStatsData?.dealsRotting ?? 0}
            />
            <MetricCard
              title={intl.formatMessage({
                id: "reports.deals.pipelineMetrics.avgDaysRotting",
                defaultMessage: "Avg. Days Rotting",
                description: "Title for avg days rotting on Deals screen.",
              })}
              value={intl.formatMessage(
                {
                  id: "reports.deals.pipelineMetrics.avgDaysRotting.content",
                  defaultMessage: "{length} {length, plural, one {Day} other {Days}}",
                  description: "Data for avg days rotting on Deals screen.",
                },
                {
                  length: intl.formatNumber(stageStatsData?.avgDaysRotting ?? 0, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }),
                }
              )}
            />
          </>
        ) : (
          <MetricCard
            title={intl.formatMessage({
              id: "reports.deals.pipelineMetrics.avgDaysToClose",
              defaultMessage: "Avg. Days to Close",
              description: "Title for Avg. Days to Close on Deals screen.",
            })}
            value={intl.formatMessage(
              {
                id: "reports.deals.pipelineMetrics.avgDaysToClose.content",
                defaultMessage: "{length} {length, plural, one {Day} other {Days}}",
                description: "Data for Avg. Days to Close on Deals screen.",
              },
              {
                length: intl.formatNumber(stageStatsData?.avgDaysToClose ?? 0, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 0,
                }),
              }
            )}
          />
        )}
      </div>

      {/* closed won/lost metrics shown only for 'all stages' */}
      {selectedStage === undefined && (
        <>
          <br />
          <div className={styles.subSection}>
            <div className={styles.subTitle}>
              <FormattedMessage
                id="reports.deals.closingMetrics"
                defaultMessage="Closing metrics for {hasFunnel, select, true {{funnel}} other {}} funnel"
                description="Title for Closing Metrics section on Deals screen."
                values={{
                  funnel: <strong>&quot;{selectedFunnel?.name}&quot;</strong>,
                  hasFunnel: !!selectedFunnel,
                }}
              />
            </div>
            <Button onClick={showStageReport} type="link">
              <FormattedMessage
                id="reports.deals.closingMetrics.detailedReport"
                defaultMessage="Detailed report"
                description="Report link for Closing Metrics section on Deals screen."
              />
            </Button>
          </div>
          <div className={dealStyles.metricSection}>
            <MetricCard
              title={intl.formatMessage({
                id: "reports.deals.closingMetrics.dealsWon",
                defaultMessage: "Deals Won",
                description: "Title for Deals Won on Deals screen.",
              })}
              value={intl.formatNumber(dealsWon, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              })}
            />

            <MetricCard
              title={intl.formatMessage({
                id: "reports.deals.closingMetrics.grossWonRevenue",
                defaultMessage: "Gross Won Revenue",
                description: "Title for Gross Won Revenue on Deals screen.",
              })}
              value={intl.formatNumber(grossWonRevenue, {
                currency: localSettings.getCurrencyCode(),
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                style: "currency",
              })}
            />

            <MetricCard
              title={intl.formatMessage({
                id: "reports.deals.closingMetrics.dealsLost",
                defaultMessage: "Deals Lost",
                description: "Title for Deals Lost on Deals screen.",
              })}
              value={intl.formatNumber(dealsLost, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              })}
            />

            <MetricCard
              title={intl.formatMessage({
                id: "reports.deals.closingMetrics.grossLostRevenue",
                defaultMessage: "Gross Lost Revenue",
                description: "Title for Gross Lost Revenue on Deals screen.",
              })}
              value={intl.formatNumber(grossLostRevenue, {
                currency: localSettings.getCurrencyCode(),
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                style: "currency",
              })}
            />
          </div>
        </>
      )}
      {/* pie charts */}
      {selectedStage === undefined && (
        <>
          <br />
          <TextWithLine>
            <FormattedMessage
              id="reports.deals.charts"
              defaultMessage="Charts"
              description="Title for Charts section on Deals screen."
            />
          </TextWithLine>
          <br />
          <Row>
            <Charts
              dealsLost={dealsLost}
              dealsWon={dealsWon}
              funnelsStatsData={funnelsStatsData}
              grossLostRevenue={grossLostRevenue}
              grossWonRevenue={grossWonRevenue}
              selectedFunnel={selectedFunnel}
              userIds={userIds}
            />
          </Row>
        </>
      )}

      {isStageReportShown && (
        <DealListModal
          funnel={selectedFunnel}
          onHide={hideStageReport}
          stage={selectedStage}
          userIds={userIds}
        />
      )}
    </>
  );
};

export default StatsForStage;
